<template>
  <div>
    <b-modal
      id="post-survey-modal"
      ref="post-survey-modal-ref"
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      @hidden="onModalHidden"
      @hide="onModalHide"
    >
      <template #modal-title>
        <div class="pts-modal-title">
          <span class="pts-step-kicker">{{ stepKicker }}</span>
        </div>
      </template>

      <!-- Submission Guard -->
      <div v-if="alreadySubmitted" class="pts-already-submitted">
        <div class="pts-already-submitted__icon">
          <i class="material-icons">check_circle</i>
        </div>
        <h5 class="pts-already-submitted__title">We&apos;ve already received your feedback for this trip.</h5>
        <h5 class="pts-already-submitted__title">
          Thank You.
        </h5>
      </div>

      <!-- Questions Load Error -->
      <div v-else-if="questionsLoadError" class="pts-warning">
        <div class="pts-warning__icon">
          <i class="material-icons">warning_amber</i>
        </div>
        <h5 class="pts-warning__title">Something went wrong loading the survey.</h5>
        <p class="pts-warning__text">Please close this window and try again.</p>
      </div>

      <!-- Exit Warning -->
      <div v-else-if="showExitWarning" class="pts-warning">
        <div class="pts-warning__icon">
          <i class="material-icons">warning_amber</i>
        </div>
        <h5 class="pts-warning__title">Are you sure you want to exit without submitting?</h5>
        <p class="pts-warning__text">Your feedback is not complete and will not be saved.</p>
        <div class="pts-warning__actions">
          <button class="pts-btn pts-btn--back" @click="cancelExit">
            <i class="material-icons vtd-icon-2">arrow_back</i> Continue survey
          </button>
          <button class="pts-btn pts-btn--exit" @click="confirmExit">
            Exit <i class="material-icons vtd-icon-2">close</i>
          </button>
        </div>
      </div>

      <!-- Successful Submit View -->
      <div v-else-if="submitted" class="pts-already-submitted">
        <StepDone :final="true" />
      </div>

      <!-- PT Survey -->
      <div v-else-if="booking && booking.user_information" class="pts-modal-body">
        <div v-if="!allSectionsComplete" class="pts-progress-bar">
          <div class="pts-progress-bar__fill" :style="{ width: progressPct + '%' }" />
        </div>

        <StepDetails
          v-if="currentStep === 'details'"
          :booking="booking"
          :pax-names="paxNames"
          :form="form"
          @update="onFormUpdate"
        />

        <StepOverview
          v-else-if="currentStep === 'overview'"
          :overview="definition.overview"
        />

        <StepSectionSelect
          v-else-if="currentStep === 'sectionSelect'"
          :sections="sections"
          :completed-sections="completedSections"
          :selected-section="selectedSection"
          :intro="definition.sectionSelect ? definition.sectionSelect.intro : ''"
          @select="onSectionSelect"
        />

        <div v-if="currentStep === 'sectionSelect' && allSectionsComplete" class="pts-general-message">
          <label class="pts-general-message__label">
            {{ definition.closing.text }}
          </label>
          <b-form-textarea
            v-model="generalMessage"
            rows="4"
            placeholder="Optional — add any comments here…"
          />
        </div>

        <StepQuestion
          v-else-if="currentStep === 'question'"
          :key="currentQuestion ? currentQuestion.id : 'question'"
          :question="currentQuestion"
          :answer="currentAnswer"
          :reveal="currentReveal"
          :follow-up-answer="currentFollowUpAnswer"
          :follow-up-reveal="currentFollowUpReveal"
          @answer="onAnswer"
          @reveal="onReveal"
          @follow-up-answer="onFollowUpAnswer"
          @follow-up-reveal="onFollowUpReveal"
        />

        <StepDone
          v-else-if="currentStep === 'done'"
          :section-title="selectedSectionTitle"
        />

        <div class="pts-modal-footer">
          <button
            v-if="showBack"
            class="pts-btn pts-btn--back"
            @click="goBack"
          >
            <i class="material-icons vtd-icon-2">arrow_back</i> Back
          </button>
          <div class="pts-footer-right">
            <button
              v-if="currentStep === 'done'"
              class="pts-btn pts-btn--next"
              @click="onSectionDone"
            >
              Continue
              <i class="material-icons vtd-icon-2">arrow_forward</i>
            </button>
            <button
              v-else-if="currentStep === 'sectionSelect' && allSectionsComplete"
              :disabled="submitting"
              class="pts-btn pts-btn--submit"
              @click="onSubmit"
            >
              <span>Submit feedback</span><i class="material-icons vtd-icon-2">check</i>
            </button>
            <button
              v-else-if="currentStep !== 'done'"
              :disabled="!canAdvance"
              class="pts-btn pts-btn--next"
              @click="goNext"
            >
              Next <i class="material-icons vtd-icon-2">arrow_forward</i>
            </button>
          </div>
        </div>

        <b-alert v-if="submitError" show variant="danger" class="mt-3">
          Something went wrong. Please try again.
        </b-alert>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { isOfficeOpen, getField14, checkPtSurveyCompleted, getSurveyDefinition } from '../services'
import StepDetails from './steps/StepDetails.vue'
import StepOverview from './steps/StepOverview.vue'
import StepSectionSelect from './steps/StepSectionSelect.vue'
import StepQuestion from './steps/StepQuestion.vue'
import StepDone from './steps/StepDone.vue'
import {
  itineraryItemsFor,
  answerKey,
  followUpOf,
  followUpShown,
  isQuestionComplete,
  totalQuestionCount,
  buildSurveyAnswers,
  surveySectionsFor,
} from './survey-flow'

// Share of the progress bar given to the details/overview steps, so the bar is not
// pinned at 0 before the first question and only reaches 100% at submission.
const INTRO_PROGRESS_SHARE = 5

// One survey for every booking. Which sections it contains (each Touring Hub tour,
// Communications & Information, future types) and their questions and copy all
// come from the API; this component only walks the definition it is given.
export default {
  components: { StepDetails, StepOverview, StepSectionSelect, StepQuestion, StepDone },
  props: {
    booking: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      // survey definition (fetched from API)
      definition: null,
      questionsLoadError: false,
      // step state
      stepIndex: 0,
      selectedSection: null,
      completedSections: [],
      answers: {},
      reveals: {},
      // form fields (Step Details)
      form: {
        passenger: null,
        email: '',
        tel1: '',
        tel2: '',
        tel3: '',
      },
      generalMessage: '',
      // duplicate-check
      checking: false,
      alreadySubmitted: false,
      // exit warning
      showExitWarning: false,
      forceClose: false,
      // submission
      submitting: false,
      submitted: false,
      submitError: false,
    }
  },
  computed: {
    ...mapGetters({
      paxNames: 'booking/getBookingPassengers',
      bookingNumber: 'booking/getBookingNumber',
    }),

    sections() {
      return this.definition ? this.definition.sections : []
    },

    sectionQuestions() {
      return this.selectedSection ? this.selectedSection.questions : []
    },

    selectedSectionTitle() {
      return this.selectedSection ? this.selectedSection.title : ''
    },

    // True for a survey with no sections at all, which goes straight to the
    // closing question.
    allSectionsComplete() {
      return this.sections.every(s => this.completedSections.includes(s.key))
    },

    // Step flow: details → overview → sectionSelect → q0..qN → done
    // (sectionSelect is always in flow; single-section surveys skip it on entry via goNext)
    // Length varies by section.
    flow() {
      const steps = ['details', 'overview', 'sectionSelect']
      this.sectionQuestions.forEach((_, i) => steps.push(`q${i}`))
      steps.push('done')
      return steps
    },

    currentStep() {
      const s = this.flow[this.stepIndex]
      if (s === 'details') return 'details'
      if (s === 'overview') return 'overview'
      if (s === 'sectionSelect') return 'sectionSelect'
      if (s === 'done') return 'done'
      return 'question'
    },

    currentQuestionIndex() {
      const s = this.flow[this.stepIndex]
      if (!s || !s.startsWith('q')) return -1
      return parseInt(s.slice(1), 10)
    },

    currentQuestion() {
      const i = this.currentQuestionIndex
      return i >= 0 ? this.sectionQuestions[i] : null
    },

    currentKey() {
      if (!this.selectedSection || !this.currentQuestion) return null
      return answerKey(this.selectedSection, this.currentQuestion.id)
    },

    currentFollowUpKey() {
      const followUp = followUpOf(this.currentQuestion)
      if (!this.selectedSection || !followUp) return null
      return answerKey(this.selectedSection, followUp.id)
    },

    currentAnswer() {
      return this.currentKey ? this.answers[this.currentKey] : undefined
    },

    currentReveal() {
      return this.currentKey ? this.reveals[this.currentKey] : undefined
    },

    currentFollowUpAnswer() {
      return this.currentFollowUpKey ? this.answers[this.currentFollowUpKey] : undefined
    },

    currentFollowUpReveal() {
      return this.currentFollowUpKey ? this.reveals[this.currentFollowUpKey] : undefined
    },

    hasCompletedDetails() {
      const email = this.form.email
      const emailValid = email.includes('*') || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
      return !!this.form.passenger && emailValid && !!this.form.tel1
    },

    hasPartialProgress() {
      return this.stepIndex > 0 && !this.submitted
    },

    showBack() {
      if (this.currentStep === 'sectionSelect' && this.allSectionsComplete) return false
      return this.stepIndex > 0 && this.currentStep !== 'done'
    },

    canAdvance() {
      if (this.checking) return false
      if (this.currentStep === 'details') return this.hasCompletedDetails
      if (this.currentStep === 'sectionSelect') return !!this.selectedSection
      if (this.currentStep === 'question') {
        return isQuestionComplete(this.currentQuestion, this.currentAnswer, this.currentFollowUpAnswer)
      }
      return true
    },

    stepKicker() {
      if (this.alreadySubmitted) return 'Completed'
      if (this.currentStep === 'details') return 'Confirm your details'
      if (this.currentStep === 'overview') return 'Feedback Overview'
      if (this.currentStep === 'sectionSelect') {
        if (this.allSectionsComplete) return 'Wider Trip Feedback'
        return 'Your feedback'
      }
      if (this.currentStep === 'question') {
        const qNum = this.currentQuestionIndex + 1
        return `${this.selectedSectionTitle} · Question ${qNum} of ${this.sectionQuestions.length}`
      }
      if (this.currentStep === 'done') return 'Complete'
      return ''
    },

    answeredQuestionCount() {
      const completed = totalQuestionCount(this.sections.filter(s => this.completedSections.includes(s.key)))
      const inProgress = this.currentStep === 'question' ? Math.max(this.currentQuestionIndex, 0) : 0
      return completed + inProgress
    },

    // Spans every section on the booking, not just the one being answered, so the
    // bar never climbs to 100% and resets between tours.
    progressPct() {
      const total = totalQuestionCount(this.sections)
      if (!total) return 0
      if (this.currentStep === 'details') return 2
      if (this.currentStep === 'overview') return INTRO_PROGRESS_SHARE
      const answered = Math.round((this.answeredQuestionCount / total) * (100 - INTRO_PROGRESS_SHARE))
      return Math.min(INTRO_PROGRESS_SHARE + answered, 100)
    },
  },
  methods: {
    ...mapActions({
      sendPtSurvey: 'contactUs/sendPtSurveyAction',
    }),

    async show() {
      this.reset()
      await this.fetchDefinition()
      this.$refs['post-survey-modal-ref'].show()
    },

    async fetchDefinition() {
      this.questionsLoadError = false
      this.definition = await getSurveyDefinition(this.bookingNumber, itineraryItemsFor(this.booking))
      this.questionsLoadError = !this.definition
    },

    reset() {
      this.stepIndex = 0
      this.selectedSection = null
      this.completedSections = []
      this.answers = {}
      this.reveals = {}
      this.generalMessage = ''
      this.checking = false
      this.alreadySubmitted = false
      this.showExitWarning = false
      this.forceClose = false
      this.submitted = false
      this.submitError = false
      this.form = {
        passenger: null,
        email: '',
        tel1: '',
        tel2: '',
        tel3: '',
      }
    },

    onModalHidden() {
      this.reset()
    },

    onModalHide(bvEvent) {
      if (this.forceClose || this.submitted) return
      if (bvEvent.trigger === 'headerclose' && this.hasPartialProgress) {
        bvEvent.preventDefault()
        this.showExitWarning = true
      }
    },

    cancelExit() {
      this.showExitWarning = false
    },

    confirmExit() {
      this.forceClose = true
      this.$refs['post-survey-modal-ref'].hide()
    },

    onFormUpdate(updates) {
      this.form = { ...this.form, ...updates }
    },

    onSectionSelect(section) {
      this.selectedSection = section
    },

    onAnswer(value) {
      if (!this.currentKey) return
      this.$set(this.answers, this.currentKey, value)
      // Changing the parent answer can hide its follow-up; drop the stale answer so
      // "Next" is not blocked by, or submitted with, a question no longer shown.
      if (this.currentFollowUpKey && !followUpShown(this.currentQuestion, value)) {
        this.$delete(this.answers, this.currentFollowUpKey)
        this.$delete(this.reveals, this.currentFollowUpKey)
      }
    },

    onReveal(value) {
      if (!this.currentKey) return
      this.$set(this.reveals, this.currentKey, value)
    },

    onFollowUpAnswer(value) {
      if (!this.currentFollowUpKey) return
      this.$set(this.answers, this.currentFollowUpKey, value)
    },

    onFollowUpReveal(value) {
      if (!this.currentFollowUpKey) return
      this.$set(this.reveals, this.currentFollowUpKey, value)
    },

    async goNext() {
      if (!this.canAdvance) return
      if (this.currentStep === 'details') {
        this.checking = true
        try {
          const completed = await checkPtSurveyCompleted(this.bookingNumber, this.form.passenger.id)
          if (completed) {
            this.alreadySubmitted = true
            return
          }
        } finally {
          this.checking = false
        }
      }
      if (this.currentStep === 'overview' && this.sections.length === 1) {
        this.selectedSection = this.sections[0]
        this.stepIndex = this.flow.indexOf('q0')
        return
      }
      this.stepIndex = Math.min(this.stepIndex + 1, this.flow.length - 1)
    },

    goBack() {
      this.stepIndex = Math.max(this.stepIndex - 1, 0)
    },

    onSectionDone() {
      if (this.selectedSection) {
        this.completedSections = [...new Set([...this.completedSections, this.selectedSection.key])]
      }
      this.selectedSection = null
      this.stepIndex = this.flow.indexOf('sectionSelect')
    },

    async onSubmit() {
      this.submitting = true
      this.submitError = false
      try {
        const officeDetails = await isOfficeOpen()
        const field14 = await getField14(this.booking.booking_information.custno)

        const payload = {
          passenger: this.form.passenger,
          passengerId: this.form.passenger ? this.form.passenger.id : '',
          email: this.form.email,
          tel1: this.form.tel1,
          tel2: this.form.tel2,
          tel3: this.form.tel3,
          isPTFeedback: true,
          category: 'Post Travel',
          sourceId: 'DVT',
          source: 'postTravelSurvey',
          CDB: this.booking.booking_information.custno,
          logname: this.booking.booking_information.logname,
          vtdate_authorised: this.booking.booking_information.vtdate_authorised,
          vtdate_return: this.booking.booking_information.vtdate_return,
          isOfficeOpen: officeDetails ? officeDetails.officeOpen : true,
          field14,
          generalMessage: this.generalMessage,
          isPTSurvey: true,
          surveyVersion: this.definition.surveyVersion,
          surveySections: surveySectionsFor(this.sections),
          surveyAnswers: buildSurveyAnswers(this.sections, this.answers, this.reveals),
        }

        // sendPtSurvey resolves to null on a failed request rather than throwing.
        const res = await this.sendPtSurvey(payload)
        if (!res || res.error) throw new Error('post-travel survey submission failed')

        this.submitted = true
        setTimeout(() => {
          if (this.$refs['post-survey-modal-ref']) this.$refs['post-survey-modal-ref'].hide()
        }, 2500)
      } catch (e) {
        this.submitError = true
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style lang="scss">
  @import "./styles.scss";
</style>
